/**
 * src/shell/agents/components/LandingPagePreview.tsx
 *
 * Sandboxed Live Preview with Device Viewports, A/B Variant Switcher,
 * and Multi-Format Export Controls.
 */

import React, { useState } from 'react';
import { LandingPageVariant } from '../types/gstack-agents-types';
import { wrapInSandboxDoc } from '../services/HtmlSanitizer';
import { agentExportPipeline } from '../services/AgentExportPipeline';

interface LandingPagePreviewProps {
  variants: LandingPageVariant[];
  productName: string;
}

export const LandingPagePreview: React.FC<LandingPagePreviewProps> = ({ variants, productName }) => {
  const [selectedVariantIdx, setSelectedVariantIdx] = useState(0);
  const [viewportMode, setViewportMode] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [exportFeedback, setExportFeedback] = useState<string | null>(null);

  const currentVariant = variants[selectedVariantIdx] || variants[0];
  if (!currentVariant) {
    return <div style={{ color: '#94a3b8', padding: 24 }}>No landing page variants generated.</div>;
  }

  const sandboxDoc = wrapInSandboxDoc(currentVariant.html, `${productName} — ${currentVariant.name}`);

  const handleExport = (format: 'html' | 'markdown' | 'figma_json') => {
    const { mimeType, payload, downloadFilename } = agentExportPipeline.exportContent(
      currentVariant.html,
      format,
      `${productName.toLowerCase().replace(/\s+/g, '-')}-${currentVariant.variantId}`
    );
    agentExportPipeline.triggerClientDownload(payload, mimeType, downloadFilename);
    setExportFeedback(`Exported ${downloadFilename}`);
    setTimeout(() => setExportFeedback(null), 3000);
  };

  const getViewportWidth = () => {
    switch (viewportMode) {
      case 'mobile':
        return '375px';
      case 'tablet':
        return '768px';
      default:
        return '100%';
    }
  };

  return (
    <div
      data-testid="landing-page-preview-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: '#0f172a',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
      }}
    >
      {/* Top Action Bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 18px',
          background: 'rgba(30, 41, 59, 0.7)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        {/* Variant Tabs */}
        <div style={{ display: 'flex', gap: 8 }}>
          {variants.map((v, idx) => (
            <button
              key={v.variantId}
              type="button"
              data-testid={`btn-variant-tab-${idx}`}
              onClick={() => setSelectedVariantIdx(idx)}
              style={{
                padding: '6px 14px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                background: selectedVariantIdx === idx ? '#3b82f6' : 'rgba(255, 255, 255, 0.06)',
                color: selectedVariantIdx === idx ? '#fff' : '#94a3b8',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              {v.name}
            </button>
          ))}
        </div>

        {/* Viewport Toggles & Export */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', background: 'rgba(0,0,0,0.3)', borderRadius: 6, padding: 2 }}>
            <button
              type="button"
              data-testid="btn-viewport-desktop"
              onClick={() => setViewportMode('desktop')}
              style={{
                padding: '4px 10px',
                fontSize: 12,
                cursor: 'pointer',
                background: viewportMode === 'desktop' ? '#475569' : 'transparent',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
              }}
            >
              🖥️ Desktop
            </button>
            <button
              type="button"
              data-testid="btn-viewport-tablet"
              onClick={() => setViewportMode('tablet')}
              style={{
                padding: '4px 10px',
                fontSize: 12,
                cursor: 'pointer',
                background: viewportMode === 'tablet' ? '#475569' : 'transparent',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
              }}
            >
              📱 Tablet
            </button>
            <button
              type="button"
              data-testid="btn-viewport-mobile"
              onClick={() => setViewportMode('mobile')}
              style={{
                padding: '4px 10px',
                fontSize: 12,
                cursor: 'pointer',
                background: viewportMode === 'mobile' ? '#475569' : 'transparent',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
              }}
            >
              📲 Mobile
            </button>
          </div>

          <button
            type="button"
            data-testid="btn-export-html"
            onClick={() => handleExport('html')}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              background: 'linear-gradient(135deg, #10b981, #059669)',
              color: '#fff',
              fontSize: 12,
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            📥 Export HTML
          </button>
          <button
            type="button"
            data-testid="btn-export-figma"
            onClick={() => handleExport('figma_json')}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              background: 'rgba(255,255,255,0.08)',
              color: '#e2e8f0',
              fontSize: 12,
              border: '1px solid rgba(255,255,255,0.15)',
              cursor: 'pointer',
            }}
          >
            🎨 Figma Tokens
          </button>
        </div>
      </div>

      {exportFeedback && (
        <div style={{ padding: '6px 18px', background: '#065f46', color: '#6ee7b7', fontSize: 12 }}>
          ✓ {exportFeedback}
        </div>
      )}

      {/* Sandboxed IFrame Preview Container */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          padding: viewportMode === 'desktop' ? 0 : 24,
          background: '#020617',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            width: getViewportWidth(),
            height: '100%',
            minHeight: '600px',
            background: '#fff',
            borderRadius: viewportMode === 'desktop' ? 0 : 12,
            overflow: 'hidden',
            boxShadow: viewportMode === 'desktop' ? 'none' : '0 20px 40px rgba(0,0,0,0.6)',
            transition: 'width 0.3s ease',
          }}
        >
          <iframe
            data-testid="landing-page-sandbox-iframe"
            title="Landing Page Preview"
            sandbox="allow-scripts"
            srcDoc={sandboxDoc}
            style={{
              width: '100%',
              height: '100%',
              minHeight: '600px',
              border: 'none',
            }}
          />
        </div>
      </div>
    </div>
  );
};
